import opfsService from './media/OPFSService';

const cache: Record<string, File> = {};

export class MediaFileCacheService {
    async saveFile(key: string, file: File): Promise<void> {
        cache[key] = file;

        try {
            await opfsService.saveFile(key, file, file.name);
        } catch (error) {
            console.debug('Could not save file to OPFS:', error);
        }
    }

    async restoreFile(key: string): Promise<File | null> {
        const opfsFile = await opfsService.getFile(key);
        if (opfsFile) {
            cache[key] = opfsFile;
            return opfsFile;
        }

        if (cache[key]) {
            if (await this.isFileValid(cache[key])) {
                return cache[key];
            } else {
                delete cache[key];
            }
        }

        return null;
    }

    private async isFileValid(file: File): Promise<boolean> {
        try {
            await file.slice(0, 1).arrayBuffer();
            return true;
        } catch (error) {
            return false;
        }
    }

    async downloadAndCache(
        url: string,
        key: string,
        fileName: string,
        onProgress?: (progress: number) => void,
        signal?: AbortSignal
    ): Promise<File> {
        const response = await fetch(url, {signal});
        if (!response.ok) {
            throw new Error('Failed to download file');
        }

        const contentLength = Number(response.headers.get('content-length') || 0);
        await opfsService.allocateSpace(contentLength);
        const handle = await opfsService.getFileHandle(key, {create: true});
        const writable = await handle.createWritable();
        const reader = response.body!.getReader();
        let received = 0;

        try {
            while (true) {
                if (signal?.aborted) {
                    await reader.cancel();
                    break;
                }
                const {done, value} = await reader.read();
                if (done) break;
                await writable.write(value);
                received += value.length;
                if (contentLength > 0) {
                    onProgress?.(received / contentLength);
                }
            }
            await writable.close();
        } catch (error) {
            try { await writable.close(); } catch {}
            if (signal?.aborted) {
                opfsService.deleteFile(key).catch(() => {});
                throw new DOMException('Download aborted', 'AbortError');
            }
            throw error;
        }

        if (signal?.aborted) {
            opfsService.deleteFile(key).catch(() => {});
            throw new DOMException('Download aborted', 'AbortError');
        }

        const file = await opfsService.finalizeFile(key, fileName);
        cache[key] = file;
        return file;
    }

    async clearAll(): Promise<void> {
        for (const key of Object.keys(cache)) {
            delete cache[key];
        }
        await opfsService.clearAll();
    }

    clearFile(key: string): void {
        if (cache[key]) {
            delete cache[key];
        }

        opfsService.deleteFile(key).catch(error => {
            console.debug('Could not remove file from OPFS:', error);
        });
    }
}

const mediaFileCacheService = new MediaFileCacheService();
export default mediaFileCacheService;
